import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, User, Users, X } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { TeamFilterParams } from './TeamFilters';
import GetTeamsQuery from './GetTeamsQuery';
import UI from '../../../common/UI';
import useFocusIndex from '../../../common/useFocusIndex';
import useOnClickOutside from '../../../common/useOnClickOutside';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export interface Team {
  id: string;
  title: string;
  team_type: {
    id: string;
  };
}

export type TeamPickerPreselector = (
  (teams: Team[])=> Team
);

export interface TeamPickerProps {
  team?: Team;
  filters?: TeamFilterParams;
  onChange?: (team: Team) => void;
  preselect?: TeamPickerPreselector;
  renderActive?: (team?: Team) => ReactNode;
  nullable?: boolean;
  disabled?: boolean;
  noneOption?: ReactNode;
}

const TeamPicker = ({
  team, filters, onChange, preselect, renderActive,
  nullable = true, disabled = false, noneOption,
}: TeamPickerProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const [limit, setLimit] = useState(20);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(filters?.search || '');
  const searchRef = useRef<HTMLInputElement>();

  const { event, ...inputFilters } = filters || {};

  const { data, loading } = useQuery(GetTeamsQuery, {
    variables: {
      project_id: project.id,
      ...inputFilters,
      events: event ? [event] : null,
      search,
      per_page: limit,
    },
    skip: !preselect && !isOpen && !team,
  });

  const teams = useMemo(
    () => data?.project.teams.data || [],
    [data?.project.teams.data],
  );

  const resetSearch = () => {
    setSearch('');

    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  /** Index 0 is reserved for 'all teams' */
  const activeIndex = teams.findIndex(({ id }) => id === team?.id) + (nullable ? 1 : 0);
  const maxIndex = teams.length - (nullable ? 0 : 1);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  const [focusIndex, setFocusIndex, listRef, scrollToIndex] = useFocusIndex({
    initialIndex: Math.max(0, activeIndex),
    maxIndex,
    active: isOpen,
    onSelect: (index) => setTeam(teams[index - (nullable ? 1 : 0)]?.id),
    onCancel: () => close(),
  });
  /* eslint-enable @typescript-eslint/no-use-before-define */

  const handleSearch = (team: ChangeEvent<HTMLInputElement>) => {
    setSearch(team.target.value);
  };

  useEffect(() => {
    const min = search ? Math.min(1, maxIndex) : 0;
    setFocusIndex(Math.max(min, activeIndex));
  }, [search, maxIndex, setFocusIndex, activeIndex, isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = () => {
    setIsOpen(true);
    scrollToIndex(focusIndex);
  };

  const setTeam = useCallback((id?: string) => {
    onChange?.(teams.filter((team) => team.id === id)[0]);
    close();
  }, [onChange, teams, close]);

  const toggleDropdown = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    }
  }, [isOpen]);

  const containerRef = useRef<HTMLDivElement>();

  useOnClickOutside(containerRef, () => {
    close();
  });

  useEffect(() => {
    if (data && preselect) {
      setTeam(preselect(teams)?.id);
    }
  }, [data, preselect, setTeam, teams]);

  return (
    <UI.PickerContainer open={isOpen} ref={containerRef}>
      <UI.PickerDropdown open={isOpen}>
        <button type="button" onClick={() => toggleDropdown()} disabled={disabled}>
          <UI.Span>
            <UI.Truncate>
              {team && (renderActive?.(team) || (
                <UI.Span>
                  <UI.Icon>
                    <Users />
                  </UI.Icon>
                  {' '}
                  {team.title}
                </UI.Span>
              ))}
              {!team && (noneOption || <UI.Span sc={{ color: 'gray.400' }}>{t('choose_team')}</UI.Span>)}
            </UI.Truncate>
            <UI.Icon>
              <ChevronDown />
            </UI.Icon>
          </UI.Span>
        </button>
        {isOpen && (
          <UI.Div>
            <UI.InputGroup sc={{ mb: 2 }}>
              <UI.InputWrapper>
                <UI.Span>
                  <UI.Icon>
                    <Search />
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder={t('search')}
                  ref={searchRef}
                  sc={{ px: 5 }}
                />
                {search && (
                  <UI.Span>
                    <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <X />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                )}
              </UI.InputWrapper>
            </UI.InputGroup>
            <UI.Div id="TeamPickerList" ref={listRef}>
              {nullable && (
                <>
                  <UI.PickerItem
                    type="button"
                    id="TeamPickerList_0"
                    onClick={() => setTeam(null)}
                    onFocus={() => setFocusIndex(0)}
                    onMouseEnter={() => setFocusIndex(0)}
                    active={activeIndex === 0}
                    focus={focusIndex === 0}
                    tabIndex={-1}
                  >
                    {noneOption || t('none')}
                  </UI.PickerItem>
                  <UI.HR />
                </>
              )}
              {!data && <UI.Loader />}
              {teams.map((team, index) => (
                <UI.PickerItem
                  type="button"
                  id={`TeamPickerList_${index + (nullable ? 1 : 0)}`}
                  onClick={() => setTeam(team.id)}
                  onFocus={() => setFocusIndex(index + (nullable ? 1 : 0))}
                  onMouseEnter={() => setFocusIndex(index + (nullable ? 1 : 0))}
                  active={activeIndex === index + (nullable ? 1 : 0)}
                  focus={focusIndex === index + (nullable ? 1 : 0)}
                  tabIndex={-1}
                  style={{ opacity: loading ? 0.5 : undefined }}
                  key={team.id}
                >
                  <UI.FlexContainer sc={{ gap: 0.25, wrap: 'wrap', alignItems: 'center' }}>
                    {team.title}
                    <UI.Span sc={{ muted: true }}>
                      <UI.Icon>
                        <User />
                      </UI.Icon>
                      {' '}
                      {team.captain.full_name}
                    </UI.Span>
                    {team.team_type.title && (
                      <UI.Label sc={{ small: true }}>
                        {team.team_type.title}
                      </UI.Label>
                    )}
                  </UI.FlexContainer>
                </UI.PickerItem>
              ))}
              {data?.project.teams.has_more_pages && (
                <>
                  <UI.HR />
                  <UI.PickerItem
                    type="button"
                    id={`TeamPickerList_${teams.length + 1}`}
                    onClick={() => setLimit((limit) => limit + 20)}
                    onFocus={() => setFocusIndex(teams.length + 1)}
                    onMouseEnter={() => setFocusIndex(teams.length + 1)}
                    active={activeIndex === teams.length + 1}
                    focus={focusIndex === teams.length + 1}
                    tabIndex={-1}
                    disabled={loading}
                  >
                    {t('show_more')}
                  </UI.PickerItem>
                </>
              )}
            </UI.Div>
          </UI.Div>
        )}
      </UI.PickerDropdown>
    </UI.PickerContainer>
  );
};

export default TeamPicker;
